import { useMemo } from "react";
import { useTellescopeQueries } from "@tellescope/tanstack-query";
import type { ChatAttachment } from "@tellescope/types-models";

interface UseEmailAttachmentsReturn {
  urlMap: Map<string, string>;
  isLoading: boolean;
  hasErrors: boolean;
}

export const useEmailAttachments = (attachments?: ChatAttachment[]): UseEmailAttachmentsReturn => {
  // Only query attachments that have a secureName
  const validAttachments = useMemo(() => attachments?.filter((att) => !!att.secureName) ?? [], [attachments]);

  const results = useTellescopeQueries({
    model: "files",
    method: "file_download_URL",
    requests: validAttachments.map<{ secureName: string }>((att) => ({ secureName: att.secureName })),
    staleTime: 4 * 60 * 1000, // 4 minutes (presigned URLs typically valid for 5 minutes)
    retry: 1, // Fast fail for missing files
  });

  const urlMap = useMemo(() => {
    const map = new Map<string, string>();

    results.forEach((result, index) => {
      if (result.data && validAttachments[index]) {
        map.set(validAttachments[index].secureName, result.data.downloadURL);
      }
    });

    return map;
  }, [results, validAttachments]);

  const isLoading = results.some((result) => result.isLoading);
  const hasErrors = results.some((result) => result.isError);

  // Log errors for debugging
  if (hasErrors) {
    results.forEach((result, index) => {
      if (result.isError) {
        console.error(`Failed to fetch attachment URL for ${validAttachments[index]?.secureName}:`, result.error);
      }
    });
  }

  return {
    urlMap,
    isLoading,
    hasErrors,
  };
};
